import type { UmbracoContentItem } from "./client";

// ---------------------------------------------------------------------------
// Block List
// ---------------------------------------------------------------------------

export interface BlockItem {
  content: {
    id: string;
    contentType: string;
    properties: Record<string, unknown>;
  };
  settings?: {
    id: string;
    contentType: string;
    properties: Record<string, unknown>;
  } | null;
}

/**
 * Extract block list items from a content item property.
 * Works with the standard Umbraco `ApiBlockListModel` shape.
 */
export function getBlockList(
  item: UmbracoContentItem,
  propertyAlias: string,
): BlockItem[] {
  const props = (item as Record<string, any>).properties ?? {};
  const blockList = props[propertyAlias];
  if (!blockList?.items || !Array.isArray(blockList.items)) return [];
  return blockList.items.filter((bi: any) => bi.content);
}

// ---------------------------------------------------------------------------
// Property accessors
// ---------------------------------------------------------------------------

/**
 * Safe typed property accessor for content items.
 */
export function getProperty<T = unknown>(
  item: UmbracoContentItem,
  alias: string,
): T | undefined {
  return ((item as Record<string, any>).properties ?? {})[alias] as
    | T
    | undefined;
}

// ---------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------

/**
 * Get the URL of the first media item from a media picker value.
 * Umbraco media pickers return `IApiMediaWithCropsModel[]`.
 */
export function getMediaUrl(value: unknown): string | null {
  if (Array.isArray(value) && value.length > 0) {
    return (value[0] as any)?.url ?? null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------

/**
 * Get the URL from a link picker value.
 * Umbraco link pickers return `ApiLinkModel[]`.
 */
export function getLinkUrl(value: unknown): string | null {
  if (Array.isArray(value) && value.length > 0) {
    const link = value[0] as any;
    return link?.url ?? link?.route?.path ?? null;
  }
  return null;
}

/**
 * Get the title (link text) from a link picker value.
 * Umbraco link pickers return `ApiLinkModel[]`.
 */
export function getLinkTitle(value: unknown): string | null {
  if (Array.isArray(value) && value.length > 0) {
    return (value[0] as any)?.title ?? null;
  }
  return null;
}

/**
 * Get the target from a link picker value (e.g. "_blank", "_self").
 * Umbraco link pickers return `ApiLinkModel[]`.
 */
export function getLinkTarget(value: unknown): string | undefined {
  if (Array.isArray(value) && value.length > 0) {
    return (value[0] as any)?.target ?? undefined;
  }
  return undefined;
}

/**
 * Get the link type from a link picker value ("Content" | "Media" | "External").
 * Umbraco link pickers return `ApiLinkModel[]`.
 */
export function getLinkType(
  value: unknown,
): "Content" | "Media" | "External" | undefined {
  if (Array.isArray(value) && value.length > 0) {
    return (value[0] as any)?.linkType ?? undefined;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// CTA Link Resolution
// ---------------------------------------------------------------------------

export interface ResolvedLink {
  /** Fully resolved href ready for use in a Link or anchor element */
  href: string;
  /** True when the link points to internal content (use next-intl Link) */
  isInternal: boolean;
  /** Link target attribute (e.g. "_blank") */
  target?: string;
}

/**
 * Resolve a CTA link picker value into a ready-to-use href.
 *
 * Centralises the Content / Media / External branching that was previously
 * duplicated in every block.  Requires the two URL-rewriting functions from
 * the `utils` package to be passed in so this module stays dependency-free.
 *
 * For **Content** links the function prefers `route.path` over `url` because
 * `route.path` always contains the Umbraco tree path (e.g. `/pages/…`) that
 * `getNonUmbracoUrl` can reliably strip, whereas `url` may already be the
 * "nice" URL or may be null depending on Umbraco configuration.
 */
export function resolveLinkHref(
  value: unknown,
  resolvers: {
    getNonUmbracoUrl: (url?: string) => string;
    getUmbracoMediaUrl: (url?: string) => string;
  },
): ResolvedLink | null {
  if (!Array.isArray(value) || value.length === 0) return null;

  const link = value[0] as any;
  const linkType: string | undefined = link?.linkType;
  const target: string | undefined = link?.target ?? undefined;

  if (linkType === "Content") {
    // Prefer route.path (always the Umbraco tree path) over url
    const rawUrl: string | null =
      link?.route?.path ?? link?.url ?? null;
    if (!rawUrl) return null;
    return {
      href: resolvers.getNonUmbracoUrl(rawUrl),
      isInternal: true,
      target,
    };
  }

  if (linkType === "Media") {
    const rawUrl: string | null = link?.url ?? null;
    if (!rawUrl) return null;
    return {
      href: resolvers.getUmbracoMediaUrl(rawUrl),
      isInternal: false,
      target: target ?? "_blank", // default to new tab for downloads
    };
  }

  // External or unknown linkType — use url as-is
  const rawUrl: string | null = link?.url ?? null;
  if (!rawUrl) return null;
  return {
    href: rawUrl,
    isInternal: false,
    target,
  };
}
